if (!customElements.get('silver-bullet-variant-content')) {
  customElements.define(
    'silver-bullet-variant-content',
    class SilverBulletVariantContent extends HTMLElement {
      connectedCallback() {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.dataset.sectionId) return;

          const source = Array.from(
            event.data.html.querySelectorAll(
              `[data-sb-variant-content][data-content-key="${this.dataset.contentKey}"]`
            )
          ).find((candidate) => candidate.dataset.sectionId === this.dataset.sectionId);

          if (source) {
            this.innerHTML = source.innerHTML;
          }
        });
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }
    }
  );
}

if (!customElements.get('silver-bullet-vehicle-intake')) {
  customElements.define(
    'silver-bullet-vehicle-intake',
    class SilverBulletVehicleIntake extends HTMLElement {
      constructor() {
        super();
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleFieldInteraction = this.handleFieldInteraction.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
      }

      connectedCallback() {
        this.disclosure = this.querySelector('[data-sb-intake-disclosure]');
        this.disclosureStatus = this.querySelector('[data-sb-intake-disclosure-status]');
        this.yearField = this.querySelector('[data-sb-year-trigger]');
        this.yearDetails = this.querySelector('[data-sb-year-details]');
        this.yearStatus = this.querySelector('[data-sb-year-status]');
        const yearIsComplete = this.syncYearDetails();
        if (yearIsComplete) this.syncConditionalFields();
        this.bindToProductForm();
        this.addEventListener('input', this.handleFieldInteraction);
        this.addEventListener('change', this.handleFieldInteraction);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.handleSubmit, true);
        this.policyInput?.removeEventListener('change', this.handleFieldInteraction);
        this.variantInput?.removeEventListener('change', this.handleFieldInteraction);
        this.removeEventListener('input', this.handleFieldInteraction);
        this.removeEventListener('change', this.handleFieldInteraction);
        this.variantChangeUnsubscriber?.();
      }

      bindToProductForm(attempt = 0) {
        if (this.form) return;

        this.form = document.getElementById(this.dataset.productFormId);
        if (!this.form && attempt < 10) {
          requestAnimationFrame(() => this.bindToProductForm(attempt + 1));
          return;
        }

        if (!this.form) return;

        this.form.addEventListener('submit', this.handleSubmit, true);
        this.productFormElement = this.form.closest('product-form');
        this.variantInput = this.form.querySelector('input[name="id"]');
        this.addToCartButtons = Array.from(this.productFormElement?.querySelectorAll('[data-add-to-cart]') || []);
        this.currentVariantAvailable = this.dataset.sbVariantAvailable === 'true';

        const productInfo = this.closest('.product__info-container') || this.closest('product-information') || document;
        this.policyInput = productInfo.querySelector(
          `input[name="properties[Policy acceptance]"][form="${this.dataset.productFormId}"]`
        );

        this.policyInput?.addEventListener('change', this.handleFieldInteraction);
        this.variantInput?.addEventListener('change', this.handleFieldInteraction);
        this.updateStickyState();
      }

      handleVariantChange(event) {
        if (event.data.sectionId !== this.dataset.sectionId) return;
        this.currentVariant = event.data.variant;
        this.currentVariantAvailable = Boolean(event.data.variant?.available);
        requestAnimationFrame(() => this.updateStickyState());
      }

      handleSubmit(event) {
        const yearIsComplete = this.syncYearDetails();
        if (yearIsComplete) this.syncConditionalFields();
        const fields = this.activeRequiredFields;
        let firstInvalidField = null;

        fields.forEach((field) => {
          if (!this.validateField(field) && !firstInvalidField) {
            firstInvalidField = field;
          }
        });

        this.updateStickyState();

        if (!firstInvalidField) {
          this.hideSummary();
          if (this.policyInput?.checked && !this.completedEventPublished) {
            const hasAccessories = Boolean(
              this.querySelector('[data-sb-choice-group] input[data-sb-reveal]:checked')
            );
            const hasOtherCondition = Boolean(
              this.querySelector(
                '[data-sb-choice-group] input[name="properties[Other equipment or modifications]"]:checked:not([value="None"])'
              )
            );
            window.SilverBullet?.fitment?.publish?.('silver_bullet:vehicle_intake_completed', {
              product_handle: this.dataset.sbProductHandle || '',
              variant_id: this.variantInput?.value || '',
              fitment_key:
                this.querySelector('[data-sb-fitment-key]')?.value ||
                this.dataset.sbProductHandle ||
                '',
              has_accessories: hasAccessories,
              has_other_condition: hasOtherCondition,
            });
            this.completedEventPublished = true;
          }
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.disclosure) this.disclosure.open = true;
        this.showSummary();

        requestAnimationFrame(() => {
          const focusTarget = this.getValidationFocusTarget(firstInvalidField);
          focusTarget.focus({ preventScroll: true });
          focusTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }

      getValidationFocusTarget(field) {
        if (field.getClientRects().length > 0) return field;

        const visibleLabel = Array.from(field.labels || []).find(
          (label) => label.getClientRects().length > 0
        );

        if (visibleLabel) {
          if (!visibleLabel.hasAttribute('tabindex')) {
            visibleLabel.setAttribute('tabindex', '-1');
          }
          return visibleLabel;
        }

        return field;
      }

      handleFieldInteraction(event) {
        const isYearField = event.target === this.yearField;
        const yearIsComplete = isYearField ? this.syncYearDetails() : !this.yearDetails?.disabled;

        if (
          yearIsComplete &&
          (isYearField ||
            event.target.closest?.('[data-sb-choice-group]') ||
            event.target.matches?.('[data-sb-photo-trigger]'))
        ) {
          this.syncConditionalFields();
        }

        if (event.target === this.policyInput && this.policyInput.checked) {
          this.productFormElement?.updateCustomPropertyAccessibility?.(this.policyInput, true);
        }

        const choiceGroup = event.target.closest?.('[data-sb-choice-group]');
        const field =
          event.target.closest?.('[data-sb-intake-required]') ||
          choiceGroup?.querySelector('[data-sb-intake-required]:not([disabled])');

        if (field && !this.isFieldDisabled(field)) {
          this.validateField(field);
        }

        const hasInvalidField = this.activeRequiredFields.some(
          (requiredField) => requiredField.getAttribute('aria-invalid') === 'true'
        );

        if (!hasInvalidField) {
          this.hideSummary();
        }

        this.updateStickyState();
      }

      syncYearDetails() {
        if (!this.yearField || !this.yearDetails) return true;

        const yearValue = this.yearField.value.trim();
        const shouldReveal = yearValue.length === 4 && this.yearField.validity.valid;

        this.yearDetails.hidden = !shouldReveal;
        this.yearDetails.disabled = !shouldReveal;
        this.yearDetails.setAttribute('aria-hidden', String(!shouldReveal));
        this.yearField.setAttribute('aria-expanded', String(shouldReveal));

        if (this.yearStatus) {
          this.yearStatus.textContent = shouldReveal
            ? `Model year ${yearValue} confirmed. Complete the installation details below.`
            : 'Select the exact year to continue to bed-liner and accessory details.';
        }

        if (!shouldReveal) {
          this.yearDetails.querySelectorAll('[data-sb-intake-required]').forEach((field) => {
            this.clearFieldValidation(field);
          });
        }

        return shouldReveal;
      }

      syncConditionalFields() {
        if (this.yearDetails?.disabled) return;

        this.querySelectorAll('[data-sb-choice-group]').forEach((choiceGroup) => {
          const checkedChoice = choiceGroup.querySelector('input[type="radio"]:checked');
          const shouldReveal = checkedChoice?.hasAttribute('data-sb-reveal') || false;
          const target = document.getElementById(choiceGroup.dataset.sbConditionalTarget);

          if (target) {
            this.setConditionalState(target, shouldReveal);
          }
        });

        this.syncPhotoUpload();
      }

      setConditionalState(container, shouldShow) {
        container.hidden = !shouldShow;

        container.querySelectorAll('input, select, textarea').forEach((field) => {
          field.disabled = !shouldShow;

          if (field.hasAttribute('data-sb-conditional-required')) {
            field.required = shouldShow;
            if (shouldShow) {
              field.setAttribute('aria-required', 'true');
            } else {
              field.removeAttribute('aria-required');
            }
          }

          if (!shouldShow) {
            if (field.type === 'checkbox' || field.type === 'radio') {
              field.checked = false;
            } else if (field.type === 'file') {
              field.value = '';
            } else {
              field.value = '';
            }
            this.clearFieldValidation(field);
          }
        });
      }

      syncPhotoUpload() {
        const trigger = this.querySelector('[data-sb-photo-trigger]');
        const photoContainer = this.querySelector('[data-sb-photo-conditional]');
        if (!trigger || !photoContainer) return;

        const photoConditions = ['Modified or repaired', 'Damage present', 'Unsure'];
        const shouldShow = !trigger.disabled && photoConditions.includes(trigger.value);
        const photoInput = photoContainer.querySelector('input[type="file"]');

        photoContainer.hidden = !shouldShow;
        if (photoInput) {
          photoInput.disabled = !shouldShow;
          if (!shouldShow) photoInput.value = '';
        }
      }

      validateField(field) {
        if (this.isFieldDisabled(field)) {
          this.clearFieldValidation(field);
          return true;
        }

        let message = '';
        const isBlankText =
          (field.tagName === 'TEXTAREA' || ['text', 'number'].includes(field.type)) &&
          field.value.trim() === '';

        if ((field.type === 'checkbox' || field.type === 'radio') && field.validity.valueMissing) {
          message = field.dataset.errorRequired;
        } else if (field.validity.valueMissing || isBlankText) {
          message = field.dataset.errorRequired;
        } else if (!field.validity.valid) {
          message = field.dataset.errorInvalid || field.dataset.errorRequired;
        }

        const fieldWrapper = field.closest('[data-sb-intake-field]');
        const error = fieldWrapper?.querySelector('[data-sb-intake-error]');

        if (message) {
          field.setAttribute('aria-invalid', 'true');
          fieldWrapper?.classList.add('sb-vehicle-intake__field--error');
          if (error) {
            error.textContent = message;
            error.hidden = false;
          }
          return false;
        }

        this.clearFieldValidation(field);
        return true;
      }

      clearFieldValidation(field) {
        const fieldWrapper = field.closest('[data-sb-intake-field]');
        const error = fieldWrapper?.querySelector('[data-sb-intake-error]');

        field.removeAttribute('aria-invalid');
        fieldWrapper?.classList.remove('sb-vehicle-intake__field--error');
        if (error) {
          error.textContent = '';
          error.hidden = true;
        }
      }

      isFieldDisabled(field) {
        return field.disabled || field.matches(':disabled');
      }

      updateStickyState() {
        this.updateDisclosureStatus();
        if (!this.addToCartButtons?.length) return;

        let label = this.addToCartButtons[0].dataset.availableLabel || window.variantStrings.addToCart;
        const variantIsSelected = Boolean(this.variantInput?.value);
        const yearIsComplete =
          Boolean(this.yearField?.value) && this.yearField.value.trim().length === 4 && this.yearField.validity.valid;

        if (!variantIsSelected || this.currentVariant === null) {
          label = 'Select bed length';
        } else if (!this.currentVariantAvailable) {
          label = window.variantStrings.soldOut;
        } else if (!yearIsComplete) {
          label = 'Select exact model year';
        } else if (!this.intakeIsComplete) {
          label = 'Complete vehicle details';
        } else if (!this.policyInput?.checked) {
          label = 'Accept order terms';
        }

        this.addToCartButtons.forEach((button) => {
          const buttonText = button.querySelector('[data-add-to-cart-text]');
          if (buttonText) buttonText.textContent = label;
          button.setAttribute('aria-label', label);
        });
      }

      updateDisclosureStatus() {
        if (!this.disclosureStatus) return;

        const yearIsComplete =
          Boolean(this.yearField?.value) &&
          this.yearField.value.trim().length === 4 &&
          this.yearField.validity.valid;

        if (!yearIsComplete) {
          this.disclosureStatus.textContent = 'Select the exact model year';
        } else if (!this.intakeIsComplete) {
          this.disclosureStatus.textContent = 'Finish installation details';
        } else {
          this.disclosureStatus.textContent = 'Truck details complete';
        }
      }

      showSummary() {
        const summary = this.querySelector('[data-sb-intake-summary]');
        if (summary) summary.hidden = false;
      }

      hideSummary() {
        const summary = this.querySelector('[data-sb-intake-summary]');
        if (summary) summary.hidden = true;
      }

      get activeRequiredFields() {
        return Array.from(this.querySelectorAll('[data-sb-intake-required]')).filter(
          (field) => !this.isFieldDisabled(field)
        );
      }

      get intakeIsComplete() {
        return this.activeRequiredFields.every((field) => field.validity.valid && field.value.trim() !== '');
      }
    }
  );
}
